import { ChevronLeft, ChevronRight, ChevronsUpDown, Menu, Search, X, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { initials } from './people';

/**
 * The menu shell shared by the operations panel and the platform console (screen 18): a card that
 * floats beside the page and folds into a column of icons, each section with its page's tabs as a
 * submenu (a shortcut: the tabs stay at the top of the page), a search that goes to any section or
 * tab (Ctrl+K), and the person signed in at the foot. On phones it is an off-canvas menu. It knows
 * nothing about permissions: whoever mounts it decides what the menu holds.
 */

/** A tab of a section: opens `?tab=<key>` of its page. */
export interface ShellTab {
  key: string;
  label: string;
  /** A counter (a `.nav-count`). */
  badge?: ReactNode;
}
export interface ShellItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Extra content after the label (a counter). */
  badge?: ReactNode;
  /** The page's tabs, in its order; the first one is what the page opens with. */
  tabs?: ShellTab[];
  /** The query parameter that picks the tab (`tab` unless the page uses another one). */
  tabParam?: string;
}
export interface ShellGroup {
  title: string;
  items: ShellItem[];
}

/** Below this width the sidebar becomes an off-canvas menu. */
const MOBILE = '(max-width: 860px)';
export function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE);
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mobile;
}

/** The menu folded into a rail of icons: remembered per browser; by default folded on laptops. */
const FOLD_KEY = 'ui.navFolded';
function useFolded() {
  const [folded, setFolded] = useState(() => {
    try {
      const saved = localStorage.getItem(FOLD_KEY);
      if (saved !== null) return saved === '1';
    } catch {
      /* storage blocked: fall back to the width */
    }
    return window.innerWidth < 1280;
  });
  const toggle = () =>
    setFolded((f) => {
      try {
        localStorage.setItem(FOLD_KEY, f ? '0' : '1');
      } catch {
        /* not remembered */
      }
      return !f;
    });
  return [folded, toggle] as const;
}

const tabHref = (item: ShellItem, tab: ShellTab) => `${item.to}?${item.tabParam ?? 'tab'}=${encodeURIComponent(tab.key)}`;
const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** «pago» in «Métodos de pago»: the part that matches, to underline it. */
function Mark({ text, q }: { text: string; q: string }) {
  const i = q ? norm(text).indexOf(norm(q)) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

interface Hit {
  item: ShellItem;
  tab: ShellTab | null;
  path: string;
  /** 0 starts with, 1 contains, 2 a tab of a section that matches. */
  score: number;
}

/** Sections and tabs whose name matches: what starts with the text first, then what contains it, then the tabs of a matching section. */
function searchMenu(groups: ShellGroup[], q: string): Hit[] {
  const nq = norm(q.trim());
  if (!nq) return [];
  const hits: Array<Hit & { order: number }> = [];
  let order = 0;
  for (const g of groups)
    for (const item of g.items) {
      const rows: Array<{ tab: ShellTab | null; label: string; path: string; parent: string }> = [
        { tab: null, label: item.label, path: g.title, parent: '' },
        ...(item.tabs ?? []).map((tab) => ({ tab, label: tab.label, path: `${g.title} › ${item.label}`, parent: item.label })),
      ];
      for (const r of rows) {
        const nl = norm(r.label);
        const score = nl.startsWith(nq) ? 0 : nl.includes(nq) ? 1 : r.parent && norm(r.parent).includes(nq) ? 2 : -1;
        if (score >= 0) hits.push({ item, tab: r.tab, path: r.path, score, order: order++ });
      }
    }
  return hits.sort((a, b) => a.score - b.score || a.order - b.order);
}

type Float = { kind: 'fly' | 'tip'; to: string; top: number; left: number };

export function Shell({
  brand,
  groups,
  user,
  onLogout,
  logoutLabel = 'Cerrar sesión',
  mobileNote,
  menuBadge,
  aboveMain,
  belowMain,
  children,
}: {
  brand: { name: string; logoUrl?: string | null };
  groups: ShellGroup[];
  user: {
    name: string;
    subtitle?: string;
    /** Adds «Mi cuenta» to the account menu (the panel's). */ accountTo?: string;
    accountLabel?: string;
  };
  onLogout: () => void;
  logoutLabel?: string;
  /** Small text next to the brand in the phone's top bar (who is signed in). */
  mobileNote?: string;
  /** A counter on the phone's menu button (what is pending behind the menu). */
  menuBadge?: ReactNode;
  aboveMain?: ReactNode;
  belowMain?: ReactNode;
  children: ReactNode;
}) {
  const uid = useId();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobile = useIsMobile();
  const [foldPref, toggleFold] = useFolded();
  const folded = foldPref && !mobile;
  const menuButton = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchButton = useRef<HTMLButtonElement>(null);
  const userButton = useRef<HTMLButtonElement>(null);
  const flyRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const current = items.find((n) => location.pathname === n.to || location.pathname.startsWith(`${n.to}/`)) ?? null;
  const currentTab = (item: ShellItem) => {
    if (item !== current || !item.tabs?.length) return null;
    const asked = new URLSearchParams(location.search).get(item.tabParam ?? 'tab');
    return item.tabs.find((t) => t.key === asked) ?? item.tabs[0]!;
  };

  // only the section you are in opens by itself; the others stay as you leave them for the session
  const [open, setOpen] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (current?.tabs?.length) setOpen((o) => (o[current.to] ? o : { ...o, [current.to]: true }));
  }, [current]);

  // the list hides its scrollbar: a fade at the top or the bottom says there is more
  const [fade, setFade] = useState({ top: false, bottom: false });
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const update = () =>
      setFade({
        top: list.scrollTop > 2,
        bottom: list.scrollTop + list.clientHeight < list.scrollHeight - 2,
      });
    update();
    list.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(list);
    for (const child of Array.from(list.children)) ro.observe(child);
    return () => {
      list.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [open, folded]);
  // the section you are in stays in sight, clear of the fades, when the page opens or the menu unfolds
  useEffect(() => {
    listRef.current?.querySelector('[aria-current="page"], [aria-current="true"]')?.scrollIntoView({ block: 'center' });
  }, [current?.to, folded]);

  // folded: the tabs of a section (or the name of one without tabs) float beside its icon
  const [float, setFloat] = useState<Float | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const showFloat = (to: string, kind: Float['kind'], el: HTMLElement) => {
    window.clearTimeout(closeTimer.current);
    const r = el.getBoundingClientRect();
    setFloat({ kind, to, top: r.top, left: r.right + 10 });
  };
  const hideFloat = (now = false) => {
    window.clearTimeout(closeTimer.current);
    if (now) setFloat(null);
    // a moment to cross the gap to the floating menu
    else closeTimer.current = window.setTimeout(() => setFloat(null), 220);
  };
  useEffect(() => {
    if (!folded) setFloat(null);
  }, [folded]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  // search: Ctrl+K / ⌘K from anywhere
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const hits = useMemo(() => searchMenu(groups, q), [groups, q]);
  const openSearch = useCallback(() => {
    setQ('');
    setActive(0);
    setSearching(true);
    setFloat(null);
  }, []);
  const closeSearch = (focusBack = true) => {
    setSearching(false);
    if (focusBack) searchButton.current?.focus();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (mobile) setMenuOpen(true);
        openSearch();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobile, openSearch]);
  const go = (to: string) => {
    setSearching(false);
    setFloat(null);
    setMenuOpen(false);
    navigate(to);
  };

  // the account menu, above the person's card
  const [account, setAccount] = useState(false);
  useEffect(() => {
    if (!account) return;
    const away = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.me-menu, .me-card')) setAccount(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [account]);

  // the off-canvas menu closes with Escape and gives focus back to its button
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || searching || account) return;
      setMenuOpen(false);
      menuButton.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen, searching, account]);

  const itemKey = (e: ReactKeyboardEvent<HTMLAnchorElement>, n: ShellItem) => {
    if (!folded || !n.tabs?.length) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      showFloat(n.to, 'fly', e.currentTarget);
      window.setTimeout(() => flyRef.current?.querySelector<HTMLElement>('a')?.focus(), 0);
    } else if (e.key === 'Escape') hideFloat(true);
  };
  const flyKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const links = Array.from(flyRef.current?.querySelectorAll<HTMLElement>('a') ?? []);
    const at = links.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      links[(at + (e.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length]?.focus();
    } else if (e.key === 'Escape' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const to = float?.to;
      hideFloat(true);
      listRef.current?.querySelector<HTMLElement>(`[data-nav="${to}"]`)?.focus();
    }
  };

  const floatItem = float ? (items.find((n) => n.to === float.to) ?? null) : null;
  const avatar = initials(user.name);
  const listId = `${uid}-results`;
  const optionId = (i: number) => `${uid}-opt-${i}`;
  return (
    <div className={`layout${folded ? ' is-folded' : ''}`}>
      <a className="skip-link" href="#main">
        Saltar al contenido
      </a>
      {mobile && menuOpen && <div className="sidebar-backdrop" onClick={() => setMenuOpen(false)} aria-hidden />}
      <div className={`sidebar-wrap${menuOpen ? ' open' : ''}`}>
        {/* off-screen on phones: inert so Tab does not walk through invisible links */}
        <aside id="sidebar" className={`sidebar${menuOpen ? ' open' : ''}${folded ? ' folded' : ''}`} inert={mobile && !menuOpen ? true : undefined}>
          {/* first in the tab order, so folding does not mean walking the whole menu */}
          {!mobile && (
            <button
              type="button"
              className="sidebar-fold"
              onClick={() => {
                setFloat(null);
                toggleFold();
              }}
              aria-label={folded ? 'Desplegar el menú' : 'Plegar el menú'}
              aria-expanded={!folded}
              aria-controls="sidebar-nav"
              title={folded ? 'Desplegar el menú' : 'Plegar el menú'}
            >
              {folded ? <ChevronRight size={14} strokeWidth={2.4} aria-hidden /> : <ChevronLeft size={14} strokeWidth={2.4} aria-hidden />}
            </button>
          )}
          <div className="brand">
            {brand.logoUrl && !folded ? (
              <img src={brand.logoUrl} alt="" height={26} />
            ) : (
              <span className="logo-dot" aria-hidden>
                {initials(brand.name).slice(0, 1)}
              </span>
            )}
            <b>{brand.name}</b>
            {mobile && (
              <button type="button" className="btn ghost icon sidebar-close" onClick={() => setMenuOpen(false)} aria-label="Cerrar menú">
                <X size={18} aria-hidden />
              </button>
            )}
          </div>
          <div className="sidebar-search">
            <button
              ref={searchButton}
              type="button"
              className="search-trigger"
              onClick={openSearch}
              aria-label="Buscar sección o pestaña"
              aria-haspopup="dialog"
              aria-expanded={searching}
              aria-keyshortcuts="Control+K Meta+K"
              title={folded ? 'Buscar (Ctrl K)' : undefined}
            >
              <Search size={16} aria-hidden />
              <span className="nav-label">Buscar</span>
              {!mobile && (
                <kbd className="nav-label" aria-hidden>
                  Ctrl K
                </kbd>
              )}
            </button>
          </div>
          <div className={`sidebar-list${fade.top ? ' fade-top' : ''}${fade.bottom ? ' fade-bottom' : ''}`}>
            <nav id="sidebar-nav" ref={listRef} aria-label="Secciones">
              {groups.map((g) => {
                if (g.items.length === 0) return null;
                const id = `${uid}-g-${g.title}`;
                return (
                  <div key={g.title} className="nav-group" role="group" aria-labelledby={id}>
                    <div id={id} className="nav-group-title">
                      <span className="nav-label">{g.title}</span>
                    </div>
                    {g.items.map((n) => {
                      const tabs = n.tabs ?? [];
                      const isCur = n === current;
                      const expanded = !folded && tabs.length > 0 && !!open[n.to];
                      const tab = currentTab(n);
                      // with an active tab in sight, the tint moves to the tab
                      const childActive = expanded && !!tab;
                      const kidsId = `${uid}-k-${n.to}`;
                      return (
                        <div key={n.to} className="nav-item">
                          <div className={`nav-row${isCur && !childActive ? ' tinted' : ''}${isCur ? ' current' : ''}`}>
                            <NavLink
                              to={n.to}
                              data-nav={n.to}
                              aria-current={isCur ? (childActive ? 'true' : 'page') : undefined}
                              aria-haspopup={folded && tabs.length ? 'true' : undefined}
                              aria-expanded={folded && tabs.length ? float?.to === n.to && float.kind === 'fly' : undefined}
                              onClick={() => {
                                setMenuOpen(false);
                                if (tabs.length && !folded) setOpen((o) => ({ ...o, [n.to]: true }));
                              }}
                              onMouseEnter={(e) => folded && showFloat(n.to, tabs.length ? 'fly' : 'tip', e.currentTarget)}
                              onMouseLeave={() => folded && hideFloat()}
                              onFocus={(e) => folded && e.currentTarget.matches(':focus-visible') && showFloat(n.to, tabs.length ? 'fly' : 'tip', e.currentTarget)}
                              onBlur={() => folded && hideFloat()}
                              onKeyDown={(e) => itemKey(e, n)}
                            >
                              <span className="nav-icon">
                                <n.icon size={18} strokeWidth={1.8} aria-hidden />
                              </span>
                              <span className="nav-label">{n.label}</span>
                              {n.badge}
                            </NavLink>
                            {tabs.length > 0 && !folded && (
                              <button
                                type="button"
                                className="nav-chevron"
                                aria-expanded={expanded}
                                aria-controls={kidsId}
                                aria-label={`Pestañas de ${n.label}`}
                                onClick={() => setOpen((o) => ({ ...o, [n.to]: !o[n.to] }))}
                              >
                                <ChevronRight size={14} aria-hidden />
                              </button>
                            )}
                          </div>
                          {expanded && (
                            <ul id={kidsId} className="nav-tabs" aria-label={`Pestañas de ${n.label}`}>
                              {tabs.map((t) => {
                                const on = isCur && tab?.key === t.key;
                                return (
                                  <li key={t.key} className={on ? 'on' : undefined}>
                                    <NavLink to={tabHref(n, t)} aria-current={on ? 'page' : undefined} className={() => (on ? 'active' : '')} onClick={() => setMenuOpen(false)}>
                                      <span className="nav-tab-label">{t.label}</span>
                                      {t.badge}
                                    </NavLink>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </nav>
          </div>
          <div className="me">
            <button
              ref={userButton}
              type="button"
              className="me-card"
              aria-haspopup="menu"
              aria-expanded={account}
              aria-controls={`${uid}-account`}
              aria-label={`Cuenta: ${user.name}`}
              onClick={() => setAccount(!account)}
              title={folded ? user.name : undefined}
            >
              <span className="me-avatar" aria-hidden>
                {avatar}
              </span>
              <span className="me-text">
                <b>{user.name}</b>
                {user.subtitle && <span className="muted">{user.subtitle}</span>}
              </span>
              <ChevronsUpDown size={16} aria-hidden className="nav-label me-chevron" />
            </button>
            {account && (
              <div
                id={`${uid}-account`}
                className="me-menu"
                role="menu"
                aria-label="Cuenta"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setAccount(false);
                    userButton.current?.focus();
                  }
                }}
              >
                {user.accountTo && (
                  <NavLink
                    role="menuitem"
                    to={user.accountTo}
                    onClick={() => {
                      setAccount(false);
                      setMenuOpen(false);
                    }}
                  >
                    {user.accountLabel ?? 'Mi cuenta'}
                  </NavLink>
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAccount(false);
                    onLogout();
                  }}
                >
                  {logoutLabel}
                </button>
              </div>
            )}
          </div>

          {float && floatItem && folded && (
            <div
              ref={flyRef}
              className={float.kind === 'fly' ? 'nav-fly' : 'nav-tip'}
              style={{ top: float.top, left: float.left }}
              onMouseEnter={() => window.clearTimeout(closeTimer.current)}
              onMouseLeave={() => hideFloat()}
              onFocus={() => window.clearTimeout(closeTimer.current)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) hideFloat();
              }}
              onKeyDown={flyKey}
              aria-hidden={float.kind === 'tip' ? true : undefined}
            >
              {float.kind === 'tip' ? (
                <>
                  {floatItem.label}
                  {floatItem.badge}
                </>
              ) : (
                <>
                  <NavLink to={floatItem.to} className="nav-fly-head" onClick={() => setFloat(null)}>
                    <span>{floatItem.label}</span>
                    {floatItem.badge}
                  </NavLink>
                  <ul aria-label={`Pestañas de ${floatItem.label}`}>
                    {(floatItem.tabs ?? []).map((t) => {
                      const on = currentTab(floatItem)?.key === t.key;
                      return (
                        <li key={t.key}>
                          <NavLink to={tabHref(floatItem, t)} className={() => (on ? 'active' : '')} aria-current={on ? 'page' : undefined} onClick={() => setFloat(null)}>
                            <span>{t.label}</span>
                            {t.badge}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          )}

          {searching && (
            <div className="nav-search" role="dialog" aria-label="Buscar sección o pestaña">
              <div className="nav-search-field">
                <Search size={16} aria-hidden />
                <input
                  autoFocus
                  role="combobox"
                  aria-expanded={hits.length > 0}
                  aria-controls={listId}
                  aria-activedescendant={hits.length ? optionId(Math.min(active, hits.length - 1)) : undefined}
                  aria-autocomplete="list"
                  aria-label="Buscar sección o pestaña"
                  placeholder="Buscar sección o pestaña"
                  autoComplete="off"
                  spellCheck={false}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setActive(0);
                  }}
                  onBlur={(e) => {
                    if (!e.currentTarget.closest('.nav-search')?.contains(e.relatedTarget as Node)) closeSearch(false);
                  }}
                  onKeyDown={(e) => {
                    const n = hits.length;
                    if (e.key === 'ArrowDown' && n) {
                      e.preventDefault();
                      setActive((a) => (a + 1) % n);
                    } else if (e.key === 'ArrowUp' && n) {
                      e.preventDefault();
                      setActive((a) => (a - 1 + n) % n);
                    } else if (e.key === 'Enter' && n) {
                      e.preventDefault();
                      const h = hits[Math.min(active, n - 1)]!;
                      go(h.tab ? tabHref(h.item, h.tab) : h.item.to);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      e.stopPropagation();
                      closeSearch();
                    }
                  }}
                />
                {mobile && (
                  <button type="button" className="btn ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => closeSearch()}>
                    Cancelar
                  </button>
                )}
              </div>
              {!q.trim() ? (
                <p className="nav-search-hint">Escribe el nombre de una sección o pestaña.</p>
              ) : hits.length === 0 ? (
                <div className="nav-search-none" role="status">
                  <b>Nada coincide con «{q.trim()}»</b>
                  <span>Busca por el nombre de una sección o pestaña del menú. Para encontrar un servicio, un conductor o un cliente, usa el buscador de su lista.</span>
                </div>
              ) : (
                <ul id={listId} role="listbox" aria-label="Secciones y pestañas" className="nav-search-results">
                  {hits.map((h, i) => {
                    const Icon = h.item.icon;
                    const on = i === Math.min(active, hits.length - 1);
                    return (
                      <li
                        key={`${h.item.to}/${h.tab?.key ?? ''}`}
                        id={optionId(i)}
                        role="option"
                        aria-selected={on}
                        className={on ? 'on' : undefined}
                        ref={(el) => {
                          if (on) el?.scrollIntoView({ block: 'nearest' });
                        }}
                        onMouseMove={() => !on && setActive(i)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => go(h.tab ? tabHref(h.item, h.tab) : h.item.to)}
                      >
                        <Icon size={16} aria-hidden />
                        <span className="nav-search-text">
                          <span>{h.score < 2 ? <Mark text={h.tab?.label ?? h.item.label} q={q.trim()} /> : (h.tab?.label ?? h.item.label)}</span>
                          <span className="muted small">{h.score === 2 ? <Mark text={h.path} q={q.trim()} /> : h.path}</span>
                        </span>
                        <span className="muted small">{h.tab ? 'Pestaña' : 'Sección'}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="sr-only" aria-live="polite">
                {q.trim() ? (hits.length === 1 ? '1 resultado' : `${hits.length} resultados`) : ''}
              </div>
              {!mobile && (
                <div className="nav-search-keys" aria-hidden>
                  <span>
                    <kbd>↑ ↓</kbd> Moverse
                  </span>
                  <span>
                    <kbd>Enter</kbd> Abrir
                  </span>
                  <span>
                    <kbd>Esc</kbd> Cerrar
                  </span>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
      <div className="main">
        <header className="mobile-top">
          <button ref={menuButton} type="button" className="btn icon" onClick={() => setMenuOpen(!menuOpen)} aria-label="Abrir menú" aria-expanded={menuOpen} aria-controls="sidebar">
            <Menu size={18} aria-hidden />
            {menuBadge}
          </button>
          {/* the page says where you are (its own heading); the bar says whose panel this is */}
          <b>{brand.name}</b>
          {mobileNote && <span className="muted small">{mobileNote}</span>}
        </header>
        {aboveMain}
        <main id="main" className="page" tabIndex={-1}>
          {children}
        </main>
        {belowMain}
      </div>
    </div>
  );
}
